import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { CodingFieldAdapter } from 'components/Assessments/PatAssessment/adapters/CodingFieldAdapter'
import { ModifierAFormField } from './formField'

/**
 * Modifier A subsection adapter for PAT Assessment
 * Handles modifier A extraction and transformation
 */
export class ModifierAAdapter extends CodingFieldAdapter {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract coding values for questions using helper method
    const question1 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_1,
      false
    )

    const question2 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_2,
      false
    )

    const question3 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_3,
      false
    )

    const question4 = this.extractSingleCodingValue(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_4,
      false
    )

    // Extract notes fields using helper method
    const notes1 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_1_NOTES
    )

    const notes2 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_2_NOTES
    )

    const notes3 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_3_NOTES
    )

    const notes4 = this.extractNotesField(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.MODIFIER_A_4_NOTES
    )

    return {
      [ModifierAFormField.MODIFIER_A_1_QUESTION]: question1,
      [ModifierAFormField.MODIFIER_A_1_NOTES]: notes1,
      [ModifierAFormField.MODIFIER_A_2_QUESTION]: question2,
      [ModifierAFormField.MODIFIER_A_2_NOTES]: notes2,
      [ModifierAFormField.MODIFIER_A_3_QUESTION]: question3,
      [ModifierAFormField.MODIFIER_A_3_NOTES]: notes3,
      [ModifierAFormField.MODIFIER_A_4_QUESTION]: question4,
      [ModifierAFormField.MODIFIER_A_4_NOTES]: notes4
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Build coding value mutations using helper method
    const question1Mutation = this.buildSingleCodingValueMutation(
      ModifierAFormField.MODIFIER_A_1_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_A_1,
      false
    )
    if (question1Mutation) {
      mutations.push(question1Mutation)
    }

    const question2Mutation = this.buildSingleCodingValueMutation(
      ModifierAFormField.MODIFIER_A_2_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_A_2,
      false
    )
    if (question2Mutation) {
      mutations.push(question2Mutation)
    }

    const question3Mutation = this.buildSingleCodingValueMutation(
      ModifierAFormField.MODIFIER_A_3_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_A_3,
      false
    )
    if (question3Mutation) {
      mutations.push(question3Mutation)
    }

    const question4Mutation = this.buildSingleCodingValueMutation(
      ModifierAFormField.MODIFIER_A_4_QUESTION,
      PatAssessmentQuestionnaireItem.MODIFIER_A_4,
      false
    )
    if (question4Mutation) {
      mutations.push(question4Mutation)
    }

    // Build notes field mutations using helper method
    const notes1Mutation = this.buildNotesFieldMutation(
      ModifierAFormField.MODIFIER_A_1_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_A_1_NOTES
    )
    if (notes1Mutation) {
      mutations.push(notes1Mutation)
    }

    const notes2Mutation = this.buildNotesFieldMutation(
      ModifierAFormField.MODIFIER_A_2_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_A_2_NOTES
    )
    if (notes2Mutation) {
      mutations.push(notes2Mutation)
    }

    const notes3Mutation = this.buildNotesFieldMutation(
      ModifierAFormField.MODIFIER_A_3_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_A_3_NOTES
    )
    if (notes3Mutation) {
      mutations.push(notes3Mutation)
    }

    const notes4Mutation = this.buildNotesFieldMutation(
      ModifierAFormField.MODIFIER_A_4_NOTES,
      PatAssessmentQuestionnaireItem.MODIFIER_A_4_NOTES
    )
    if (notes4Mutation) {
      mutations.push(notes4Mutation)
    }

    return mutations
  }
}
